import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Printer, X } from "lucide-react";

const CAT_LABELS = { cx1: "CX1", cx2: "CX2 - Pequena", cx3: "CX3 - Média", cx3g: "CX3G - Grande", cx4: "CX4 - Extra" };

export default function PalletLabel() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pallet, setPallet] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: p } = await api.get(`/pallets/${id}`);
        setPallet(p);
        const { data: qr } = await api.get(`/pallets/${id}/qr`);
        setQrData(qr);
        // Se tiver invoice ligado, busca dados do fornecedor
        const invId = (p.itens || [])[0]?.invoice_id;
        if (invId) {
          try { const { data: i } = await api.get(`/invoices/${invId}`); setInv(i); } catch {/* ignore */}
        }
        try { const { data: c } = await api.get(`/company`); setCompany(c); } catch {/* ignore */}
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const print = () => window.print();

  if (loading) return <div className="p-8 text-center text-ink-500">Carregando etiqueta...</div>;
  if (err) return <div className="p-8"><div className="text-danger">{err}</div></div>;
  if (!pallet) return <div className="p-8">Pallet não encontrado.</div>;

  const it = (pallet.itens || [])[0] || {};
  const isGranel = !!pallet.granel || it.categoria === "granel";
  const categoria = isGranel ? "GRANEL" : (CAT_LABELS[it.categoria] || it.categoria);
  const qtdCx = Number(it.quantidade) || 0;
  const pesoKg = isGranel ? qtdCx : qtdCx * 20; // granel: quantidade JÁ está em kg
  const dataFmt = (pallet.created_at || "").slice(0, 10).split("-").reverse().join("/");
  const isTerc = pallet.tipo_operacao === "terceiros" || inv?.tipo_operacao === "terceiros";
  const clienteDono = pallet.cliente_dono || inv?.cliente_dono || "";

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      {/* Barra de ações (some na impressão) */}
      <div className="print:hidden bg-white border-b border-ink-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => nav(-1)} className="p-2 hover:bg-stone-100 rounded flex items-center gap-2 text-sm" data-testid="close-label-btn">
          <X size={16} /> Fechar
        </button>
        <div className="text-sm text-ink-700 font-mono">Etiqueta {pallet.numero}</div>
        <button onClick={print} className="px-4 py-2 rounded-md bg-brand-500 text-white text-sm font-semibold hover:opacity-90 flex items-center gap-2" data-testid="print-btn">
          <Printer size={16} /> Imprimir
        </button>
      </div>

      {/* Etiqueta (10cm x 15cm aproximado) */}
      <div className="flex justify-center py-8 print:py-0">
        <div className={`bg-white border-2 rounded-xl w-[400px] print:w-[10cm] print:h-[15cm] shadow-lg print:shadow-none ${isTerc ? "border-purple-700 print:border-purple-900" : "border-ink-900 print:border-black"}`} data-testid="pallet-label">
          {/* Header */}
          <div className={`text-white p-4 rounded-t-xl print:rounded-none text-center ${isTerc ? "bg-purple-700" : "bg-brand-500"}`}>
            <div className="text-xl font-bold tracking-wider">{company?.nome_fantasia || "CEBOLAS SERENATA"}</div>
            <div className="text-xs opacity-90 mt-1 uppercase tracking-widest">
              {isTerc ? "Beneficiamento a Terceiros" : "Rastreabilidade de Lote"}
            </div>
          </div>

          {isTerc && (
            <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 text-center" data-testid="terceiros-badge">
              <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">Mercadoria de Terceiros</div>
              <div className="font-bold text-purple-900 text-sm">{clienteDono || "—"}</div>
            </div>
          )}

          {/* NF Info */}
          <div className="p-4 border-b border-ink-200">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">Nota Fiscal</div>
                <div className="font-mono font-bold text-lg">#{inv?.nota_numero || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">Data</div>
                <div className="font-mono">{dataFmt}</div>
              </div>
            </div>
            <div className="mt-2">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">{isTerc ? "Cliente (dono)" : "Fornecedor"}</div>
              <div className="font-medium text-ink-900">{isTerc ? (clienteDono || "—") : (inv?.fornecedor || pallet.fornecedor || "—")}</div>
            </div>
          </div>

          {/* QR + Info Categoria */}
          <div className="p-4 flex items-center gap-4">
            <div className="flex-shrink-0">
              {qrData?.data_url ? (
                <img src={qrData.data_url} alt="QR Code" className="w-32 h-32 border border-ink-200" data-testid="qr-code-img" />
              ) : (
                <div className="w-32 h-32 border border-dashed border-ink-300 flex items-center justify-center text-xs text-ink-500">QR</div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-bold">{isGranel ? "Tipo" : "Categoria"}</div>
              <div className="font-bold text-xl leading-tight">{categoria}</div>
              <div className="mt-3 text-xs uppercase tracking-wider text-ink-500 font-bold">Pallet</div>
              <div className="font-mono font-bold text-lg">{pallet.numero}</div>
              <div className="mt-3 text-xs uppercase tracking-wider text-ink-500 font-bold">{isGranel ? "Peso da Carga" : "Quantidade"}</div>
              {isGranel ? (
                <div className="font-bold text-2xl text-amber-700">{pesoKg.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</div>
              ) : (
                <>
                  <div className="font-bold text-lg">{qtdCx} sacos</div>
                  <div className="font-mono text-sm text-ink-500">≈ {pesoKg} kg</div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 bg-stone-50 border-t border-ink-200 rounded-b-xl print:rounded-none text-center">
            <div className="text-[10px] font-mono text-ink-500 break-all">ID: {pallet.id.slice(0, 8)}</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: 10cm 15cm; margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
}
